<template>
  <div v-if="businessFundingConfig !== undefined">
    <IconAppAdvancePlusBig class="mb-4 h-12 w-auto" />
    <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
      <div
        class="order-1 flex flex-col items-start gap-4 rounded-lg bg-background px-4 py-6 md:col-span-2"
      >
        <p class="text-3xl font-semibold">Great news!</p>
        <p
          class="text-base font-bold leading-tight text-background-fg/70"
        >
          Your business could be elligible for an advance of
          up to:
        </p>
        <div
          v-if="youlendOffer"
          class="flex w-full items-start justify-center rounded-lg bg-background-fg/5 p-4 text-center text-3xl font-bold text-primary"
        >
          {{
            formatToMerchantCurrencyString(youlendOffer, {
              stripTrailingZeros: true,
            })
          }}<span class="text-lg">*</span>
        </div>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
          <p
            class="col-span-2 text-base font-medium leading-tight text-background-fg/70"
          >
            Apply now and you could receive a firm offer
            from our partners within 24 hours.
          </p>
          <div class="col-span-1">
            <a
              v-if="
                businessFundingConfig.businessFundingUrl
              "
              class="relative flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-full bg-primary px-3 py-2 text-lg font-semibold text-primary-fg disabled:opacity-40"
              :href="
                businessFundingConfig.businessFundingUrl
              "
              target="_blank"
            >
              Apply Now
            </a>
          </div>
        </div>
      </div>

      <div
        class="order-3 flex flex-col items-start gap-4 rounded-lg bg-background p-4 md:order-2 md:col-span-1"
      >
        <div class="p-2">
          <img
            src="@assets/business-funding/FundedIcon.png"
            class="size-[4.5rem]"
          />
        </div>
        <p class="text-5xl font-bold text-primary">
          {{
            formatToMerchantCurrencyString(
              businessFundingConfig.fundedAmount * 100,
              { compact: true, stripTrailingZeros: true }
            )
          }}+
        </p>
        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          has already been obtained by
          <strong>take</strong>payments customers just like
          you.<sup class="text-xs">†</sup>
        </p>
      </div>

      <div
        class="order-2 grid items-start gap-4 rounded-lg bg-background px-4 py-6 md:order-3 md:col-span-2 md:grid-cols-2"
      >
        <div
          class="flex h-full flex-col items-start justify-center"
        >
          <p class="mb-4 text-2xl font-semibold">
            How does it work?
          </p>
          <p
            class="text-base font-medium leading-tight text-background-fg/70"
          >
            Repayments are based on a fixed percentage of
            your daily card takings. If you've had a mega
            month, you'll pay back a bit more, and if it's
            been on the quieter side, you'll pay back a bit
            less.
          </p>
        </div>
        <div
          class="flex h-full flex-col items-end justify-center gap-2 p-2"
        >
          <img
            src="@assets/business-funding/BusinessFundingGraph.png"
            class="mt-4"
          />
          <div>
            <div class="flex items-center">
              <div class="h-0.5 w-4 bg-cash" />
              <div class="size-3 rounded-full bg-cash" />
              <p
                class="ml-2 font-semibold text-background-fg/70"
              >
                Revenue
              </p>
            </div>
            <div class="flex items-center">
              <div
                class="w-4 border border-dashed border-background-fg/60"
              />
              <div
                class="size-3 rounded-full border-2 border-background-fg/60"
              />
              <p
                class="ml-2 font-semibold text-background-fg/70"
              >
                Repayment
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="order-4 flex flex-col items-start gap-4 rounded-lg bg-background p-4 md:col-span-1"
      >
        <div class="p-2">
          <img
            src="@assets/business-funding/AdvanceFlexibleIcon.png"
            class="size-[4.5rem]"
          />
        </div>

        <p class="mt-auto text-5xl font-bold text-cash">
          {{
            formatNumber(
              businessFundingConfig.fundedNumber
            )
          }}+
        </p>

        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          Over
          {{
            formatNumber(businessFundingConfig.fundedNumber)
          }}
          <strong>take</strong>payments customers have
          benefited from <strong>Advance+</strong> flexbile
          finance so far.<sup class="text-xs">†</sup>
        </p>
      </div>

      <div
        class="order-last flex flex-col items-start gap-4 rounded-lg bg-background p-4 md:col-span-2"
      >
        <div class="p-2">
          <img
            src="@assets/business-funding/CustomerServiceIcon.png"
            class="size-[4.5rem]"
          />
        </div>

        <p class="text-2xl font-semibold">
          Have a question?
        </p>

        <p
          class="text-base font-medium leading-tight text-background-fg/70"
        >
          Get in touch with our
          <strong>Advance+</strong> Team and they'll be
          happy to help...
        </p>

        <div>
          <p class="text-4xl font-semibold text-primary">
            {{ businessFundingConfig.contactPhone }}
          </p>

          <p class="text-base font-medium">
            {{ businessFundingConfig.contactEmail }}
          </p>
        </div>
      </div>

      <div class="order-last px-4 text-xs md:col-span-3">
        <p class="font-semibold">
          <sup>†</sup>Terms and Conditions apply
        </p>
        <p class="leading-5">
          Correct as of
          {{
            DateFns.format(
              businessFundingConfig.date,
              "MMMM yyyy"
            )
          }}. takepayments effects introductions to YouLend
          Limited, which is the provider of the Loan Advance
          and Cash Advance product. Funding and the funding
          amount
        </p>
        <p class="mt-2 font-semibold">
          *Terms and Conditions apply
        </p>
        <p class="leading-5">
          The potential funding amount shown is indicative
          only and calculated using only the amount of card
          transactions (via takepayments' terminals or
          facilities) up to a period of 3 months prior to
          {{
            DateFns.format(
              businessFundingConfig.date,
              "MMMM yyyy"
            )
          }}
          and does not consider any other circumstances.
          Businesses must have been trading for 6 months or
          more and be currently open, with a minimum of
          £1,500 in card transactions per month. Other
          eligibility criteria may apply. takepayments is
          acting as an introducer and not a lender.
          takepayments effects introductions to YouLend
          Limited, which is the provider of the Cash Advance
          and Loan Advance product. Funding and the funding
          amount are subject to Youlend's approval.
        </p>
      </div>
    </div>
  </div>
  <BaseInnerLoading v-else />
</template>

<script setup lang="ts">
import { IconAppAdvancePlusBig } from "@/icons"
const { youlendOffer } = useTenant()
const { formatToMerchantCurrencyString } = useCurrency()

const { data: businessFundingConfig } =
  useBusinessFundingConfig()

const formatNumber = (number: number) => {
  const localeBcp47 = "en-GB"
  return new Intl.NumberFormat(localeBcp47, {
    maximumSignificantDigits: 3,
  }).format(number)
}
</script>
